import {useCallback, useEffect, useState} from "react";
import {createDownloadRequest} from "services/http/createDownloadRequest";
import createHash from "services/http/createHash";
import createRequestHandler from "./createRequestHandler";
import type {
  ApiError,
  ArgsAllowed,
  ConfigOrPath,
  DownloadHookState,
  DownloadOptions,
  ImmediateHookState,
  LazyHookState,
  Method,
  RequestOptions,
  unknownRecord,
} from "./types";

const createSendRequest = <Res, Args extends ArgsAllowed = void>(
  method: Method,
  configOrPath: ConfigOrPath,
) => {
  const baseConfig =
    typeof configOrPath === "string"
      ? {path: configOrPath, method}
      : {...configOrPath, method};

  return () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<ApiError | null>(null);
    const [data, setData] = useState<Res | null>(null);
    const [headers, setHeaders] = useState<Record<string, string>>({});

    const run = useCallback(
      async (args: Args, options?: {queryParams?: unknownRecord}) => {
        const config = {
          ...baseConfig,
          queryParams: options?.queryParams,
        };
        const sendRequest = createRequestHandler<Res, Args>(config);

        setLoading(true);
        setError(null);
        try {
          const result = await sendRequest(args);

          let payload: any = result;
          let resHeaders: Record<string, string> = {};

          if (
            result &&
            typeof result === "object" &&
            "payload" in result &&
            "headers" in result
          ) {
            payload = result.payload as Res;
            resHeaders = result.headers as Record<string, string>;
          }

          setData(payload as Res);
          setHeaders(resHeaders ?? {});
          return payload as Res;
        } catch (err) {
          setError(err as ApiError);
          throw err;
        } finally {
          setLoading(false);
        }
      },
      [],
    );

    return {run, loading, error, data, headers, setData} as const;
  };
};

const createLazyRequestHook =
  (method: Method) =>
  <Res = void, Args extends ArgsAllowed = void>(
    configOrPath: ConfigOrPath,
  ): (() => Readonly<
    [
      (args: Args, options?: {queryParams?: unknownRecord}) => Promise<Res>,
      LazyHookState<Res>,
    ]
  >) => {
    const useSendRequest = createSendRequest<Res, Args>(method, configOrPath);
    return () => {
      const {run, ...state} = useSendRequest();
      return [run, state] as const;
    };
  };

const createDownloadHook =
  (method: Method) =>
  <Args extends ArgsAllowed = void>(
    configOrPath: ConfigOrPath,
  ): (() => Readonly<
    [
      (args: Args, options?: DownloadOptions) => Promise<void>,
      DownloadHookState,
    ]
  >) => {
    const useDownloadRequest = createDownloadRequest<Args>(
      method,
      configOrPath,
    );
    return () => {
      const {download, ...state} = useDownloadRequest();
      return [download, state] as const;
    };
  };

export const useStableValue = <T>(value: T): T => {
  const hash = createHash(value);

  const [stable, setStable] = useState({value, hash});

  if (stable.hash !== hash) {
    setStable({value, hash});

    return value;
  }
  return stable.value;
};

const createImmediateRequestHook = <
  Res = void,
  Args extends ArgsAllowed = void,
>(
  configOrPath: ConfigOrPath,
): ((
  args: Args,
  options?: RequestOptions,
) => Readonly<ImmediateHookState<Res>>) => {
  const useSendRequest = createSendRequest<Res, Args>("GET", configOrPath);

  return (args: Args, options?: {skip?: boolean}) => {
    const {skip} = options ?? {};
    const {run, ...state} = useSendRequest();

    const memoizedArgs = useStableValue(args);

    const execute = useCallback(() => run(memoizedArgs), [memoizedArgs, run]);

    useEffect(() => {
      if (!skip) execute();
    }, [skip, execute]);

    return {...state, execute} as const;
  };
};

export const createPostHook = createLazyRequestHook("POST");
export const createPatchHook = createLazyRequestHook("PATCH");
export const createDeleteHook = createLazyRequestHook("DELETE");
export const createLazyGetHook = createLazyRequestHook("GET");
export const createGetHook = createImmediateRequestHook;

export const createDownloadGetHook = createDownloadHook("GET");
